import {
    AdaptableAngularAgGridModule,
    AdaptableApi,
    AdaptableOptions,
    AdaptableReadyInfo,
    AdaptableState,
    AdaptableStateChangedInfo,
    AdaptableStateFunctionConfig,
    AdaptableUpgradeHelper,
    CalculatedColumn,
    CustomContextMenuContext,
    PivotLayout,
    ReportFileNameContext,
    TableLayout,
    UserContextMenuItem
} from '@adaptabletools/adaptable-angular-aggrid';
import { ChangeDetectorRef, Component, effect, EventEmitter, inject, OnDestroy, signal } from '@angular/core';
import { Store } from '@ngrx/store';
import { IStatusBarMessageComp, NavigationTabsService, ReportTabActions, ReportTabSelectors } from '@unified-ui/shared-components';
import { defaultAdaptableOptions, patchCsvExport, patchExcelExport, ReportDataDialog } from '@unified-ui/utilities';

import { AgChartsEnterpriseModule } from 'ag-charts-enterprise';
import { AgGridModule } from 'ag-grid-angular';
import { ColDef, GridApi, GridReadyEvent, IRowNode, Module } from 'ag-grid-community';
import { AllEnterpriseModule, SparklinesModule } from 'ag-grid-enterprise';
import { BehaviorSubject, combineLatest, debounceTime, distinctUntilChanged, EMPTY, filter, fromEventPattern, map, Observable, Observer, skipUntil, Subject, Subscription, switchMap, take, takeUntil, tap, timer } from 'rxjs';

import { BuilderManagerService, UserInputErrorService } from '@unified-ui/shared/services';
import { DialogModule } from 'primeng/dialog';

export enum ReportLayoutNames {
    TABLE_LAYOUT = 'Table Layout',
    PIVOT_LAYOUT = 'Pivot Layout'
}

export enum ReportLayoutNamesLegacy {
    DEFAULT_LAYOUT = 'Default Layout',
    DEFAULT_PIVOT = 'Default Pivot'
}

/**
 * Handles base functionalities and report dependencies that are not feature specific, or
 * does not uses types from the features. Protected methods are / can be consumed by children, like
 * all the loading validation cycle functions and observables.
 */
@Component({
    selector: 'report-base-component',
    standalone: true,
    template: '',
    imports: [AgGridModule, AdaptableAngularAgGridModule, DialogModule]
})
/**
 * ReportBaseComponent
 * -------------------
 * Centralizes Adaptable event registration and cleanup for all report components.
 *
 * - Subclasses must override `onAdaptableStateChanged` for custom event logic.
 * - Do NOT register or deregister Adaptable events in subclasses.
 * - Cleanup of Adaptable event handlers is handled automatically in ngOnDestroy.
 *
 * Example usage in a subclass:
 * ```ts
 * protected override onAdaptableStateChanged(eventInfo: AdaptableStateChangedInfo): void {
 *   if (eventInfo.actionName === 'FORMAT_COLUMN_ADD') {
 *     // custom logic here
 *   }
 * }
 * ```
 */
export abstract class ReportBaseComponent implements OnDestroy {
    /** Columns that should use average aggregation instead of sum */
    private static readonly AVG_AGGREGATION_COLUMNS = ['GRPs', 'NEP', 'TRPs'];

    /**
     * RxJS BehaviorSubjects for reactive state management
     * These replace the boolean flags with observable streams that can be combined and transformed
     */
    // Track when the adaptable component is ready
    protected readonly adaptableReady$ = new BehaviorSubject<boolean>(false);

    // Track when column sizing is complete
    protected readonly columnSizingComplete$ = new BehaviorSubject<boolean>(false);

    // Track when the grid is fully ready with data
    protected readonly gridDataReady$ = new BehaviorSubject<boolean>(false);

    /**
     * Combined observable that determines when all components are ready
     * This replaces the _allComponentsReady and bothComponentsReady methods
     */
    protected readonly allComponentsReady$ = combineLatest([this.gridDataReady$, this.adaptableReady$, this.columnSizingComplete$]).pipe(
        map(([gridReady, adaptableReady, columnSizingComplete]) => gridReady && adaptableReady && columnSizingComplete),
        distinctUntilChanged()
    );

    // Flag to prevent multiple loading state changes during initialization
    protected readonly isInitialLoadComplete$ = new BehaviorSubject<boolean>(false);

    // Track if we're in the process of initializing the grid
    protected readonly isInitializing$ = new BehaviorSubject<boolean>(false);

    // Test property for debugging skeleton visibility
    protected readonly testLoading$ = new BehaviorSubject<boolean>(false);

    /**
     * Combined observable that determines if the component should be in loading state
     * This replaces the manual loading state management in the effect
     */
    protected readonly shouldBeLoading$ = combineLatest([this.testLoading$, this.isInitializing$, this.gridDataReady$, this.columnSizingComplete$]).pipe(
        map(([testLoading, isInitializing, gridReady, columnSizingComplete]) => {
            return testLoading || !gridReady || !columnSizingComplete || isInitializing;
        }),
        distinctUntilChanged(),
        debounceTime(300) // Built-in debounce instead of manual timing
    );

    /** Adaptable API - prefer using this api instead ag grid when possible */
    protected adaptableApi!: AdaptableApi;

    /** Visibility of the dialog for saving a report */
    protected isSaveDialogVisible = false;

    /** Report reference with criteria and properties from IReport */
    protected report: ReportDataDialog = {};

    /** Adaptable Options */
    protected adaptableOpts: AdaptableOptions<any> = {
        ...defaultAdaptableOptions,
        adaptableId: 'Report: AdaptableID is not being updated',
        stateOptions: {
            // give AdapTable full control over load/save so we can migrate:
            loadState: async ({ adaptableId }) => {
                const existing = localStorage.getItem(adaptableId);
                // Hold off on using the indexedDB until findings from PFD-7429 and PFD-7434
                // this.safeSubscribe(this._dbService.getReportSettings(this.currentTabId), state => {
                //     return state;
                // });

                // PFD-7420: Loading from the stored settings caused the initializing issue
                // In our case, we are providing the state from the backend on report load
                // and creating the state when we create a new report, so we just return null
                // try {
                //     return existing ? JSON.parse(existing) : null;
                // } catch (error) {
                return null;
                // }
            },
            persistState: async (state: Partial<AdaptableState>, config: AdaptableStateFunctionConfig) => {
                const key = this.buildReportTitleKey();
                const previousStateId = this.buildReportTitleKey(this.reportPreviousName);
                if (previousStateId) {
                    localStorage.removeItem(previousStateId);
                }
                localStorage.setItem(key, JSON.stringify(state));
            }
        },
        dashboardOptions: {
            ...defaultAdaptableOptions.dashboardOptions,
            customDashboardButtons: [
                {
                    tooltip: 'Auto resize to default',
                    icon: {
                        name: 'columns'
                    },
                    onClick: () => {
                        this.adaptableApi.columnApi.autosizeAllColumns();
                    }
                },
                {
                    tooltip: 'Collapse all',
                    icon: {
                        name: 'collapse'
                    },
                    onClick: () => this._collapseExpand(false)
                },
                {
                    tooltip: 'Expand all',
                    icon: {
                        name: 'expand'
                    },
                    onClick: () => this._collapseExpand(true)
                },
                {
                    buttonStyle: {
                        className: 'no-hover'
                    },
                    icon: {
                        src: 'assets/images/divider.svg',
                        className: 'no-hover'
                    }
                },
                {
                    tooltip: 'Save report',
                    icon: {
                        name: 'save'
                    },
                    onClick: () => {
                        this.report = { ...this.report };
                        this.isSaveDialogVisible = true;
                        this._changeDetectorReference.detectChanges();
                    }
                }
            ]
        },
        exportOptions: {
            ...defaultAdaptableOptions.exportOptions,
            reportFilename: (reportFileNameContext: ReportFileNameContext) => {
                const reportName = this.report.name ?? reportFileNameContext.reportName;
                return reportName;
            }
        },
        contextMenuOptions: {
            customContextMenu: (context: CustomContextMenuContext) => {
                const { defaultAgGridMenuStructure, defaultAdaptableMenuStructure, isRowGroupColumn } = context;
                const changeGroupExpansionMenuItem: UserContextMenuItem = {
                    menuType: 'User',
                    label: context.rowNode.expanded ? `Collapse all ${context.rowNode.rowGroupColumn?.getColDef().headerName} groups` : `Expand all ${context.rowNode.rowGroupColumn?.getColDef().headerName} groups`,
                    icon: { name: 'chevron' },
                    hidden: !isRowGroupColumn,
                    onClick: () => this._expandNodeByGroupLevel(context.rowNode, !context.rowNode.expanded)
                };
                return [
                    changeGroupExpansionMenuItem,
                    ...defaultAgGridMenuStructure.map(item => (item.menuType === 'Group' && item.label === 'Copy & Paste' ? { ...item, subMenuItems: item.subMenuItems.filter(sub => sub.name === 'copy' || sub.name === 'copyWithHeaders') } : item)), // Filters copy & paste options
                    ...defaultAdaptableMenuStructure
                ];
            }
        }
    };

    /** Ag Grid modules */
    protected agGridModules: Module[] = [AllEnterpriseModule.with(AgChartsEnterpriseModule), SparklinesModule.with(AgChartsEnterpriseModule)];

    /** Emits to close an unsaved tab */
    protected closeUnsavedTab = new EventEmitter<string>();

    /** Current tab opened tab id */
    protected currentTabId = '';

    /** Subject that emits when the component is being destroyed */
    protected destroy$ = new Subject<void>();

    /** Grid API - prefer using adaptable api when possible */
    protected gridApi!: GridApi;

    /** Data with columns and rows */
    protected gridData = <any>{
        columns: [],
        rows: []
    };

    /** Controls if the adaptable layout is ready for detection so we can listen to state changes,
     * for example, if it is refreshing data, it can be false to avoid state changes */
    protected isAdaptableLayoutReadyForStateChanges$ = new BehaviorSubject<boolean>(false);

    /** Visibility of dialog of closing an unsaved report */
    protected isClosingConfirmationVisible = false;

    /** If the data is loading */
    protected isLoading = signal<boolean>(true);

    /** If it is refreshing data to rebuild only rows */
    protected isRefreshingData = false;

    /** Emits to open the specific (second) save dialog */
    protected openSaveDialog = new EventEmitter<string>();

    /** Report type name */
    protected reportTypeName: string = 'Generic';

    /** Tab name to use in the report, if there is none */
    protected tabName?: string;

    // Previous report name for to delete previous persistence instance when changing report name
    protected reportPreviousName = '';

    // Overridden by the child components to set a different message on status bar
    protected customStatusBarMessage?: string;

    // Flag to track problematic subscriptions
    private _hasAdaptableSubscription = false;

    // Store individual subscriptions for safer cleanup
    private _individualSubscriptions: Subscription[] = [];

    /**
     * Private bound handler for AdaptableStateChanged, used for registration and cleanup.
     */
    // Flag to track if the component has been destroyed
    private _isDestroyed = false;

    // Track if we need to do a full grid initialization
    private _needsFullInitialization = true;

    // Timer reference for showing the grid
    private _showGridTimer: Subscription | null = null;

    /** Subscription collection for proper cleanup - private to enforce use of _safeAdd and safeSubscribe */
    private _subscriptions = new Subscription();

    protected store = inject(Store);
    protected _navigationTabsService = inject(NavigationTabsService);
    protected _userInputErrorService = inject(UserInputErrorService);
    protected _changeDetectorReference = inject(ChangeDetectorRef);
    protected _builderManagerService = inject(BuilderManagerService);

    constructor() {
        // Subscribe to the shouldBeLoading$ observable to update the loading state
        this.safeSubscribe(this.shouldBeLoading$, shouldBeLoading => {
            const isLoading = this.isInitialLoadComplete$.value ? shouldBeLoading : true;
            this.isLoading.set(isLoading);
        });

        // Subscribe to allComponentsReady$ to check if we should show the grid
        this.safeSubscribe(this.allComponentsReady$.pipe(filter(ready => ready)), () => {
            this.checkIfShouldShowGrid();
        });
        effect(() => {
            this.isClosingConfirmationVisible = this._builderManagerService.isExistInUnsavedWarningState(this.currentTabId);
            this._changeDetectorReference.detectChanges();
        });
        // closes tab if id matches and report/tab is invalid
        effect(() => {
            const invalidTab = this._navigationTabsService._closeInvalidTab();
            if (this.currentTabId && invalidTab === this.currentTabId) {
                this.store
                    .select(ReportTabSelectors.getTabInfo)
                    .pipe(take(1))
                    .subscribe(tabInfo => {
                        if (tabInfo[this.currentTabId].invalid) {
                            this.store.dispatch(ReportTabActions.closeTabAction({ id: this.currentTabId }));
                            this._userInputErrorService.reset();
                        }
                    });
            }
        });
    }

    public ngOnDestroy(): void {
        try {
            // Mark component as destroyed to prevent further event handling
            this._isDestroyed = true;

            // Signal all takeUntil operators that the component is being destroyed
            // This will automatically complete all subscriptions using takeUntil(this.destroy$)
            this.destroy$.next();
            this.destroy$.complete();

            // Clear the adaptableApi reference to prevent callbacks from accessing it
            // Do this BEFORE trying to unsubscribe to avoid the "Cannot read properties of null" error
            localStorage.removeItem(this.adaptableOpts.adaptableId as string);
            this.adaptableApi = null as any;

            // Create a new empty Subscription object without trying to unsubscribe from the old one
            // This avoids the "Cannot read properties of null (reading 'off')" error
            // while still allowing the garbage collector to clean up resources
            this._subscriptions = new Subscription();

            // Clear our tracking array without trying to unsubscribe
            this._individualSubscriptions = [];
        } catch (e) {
            // Catch any unexpected errors during cleanup to prevent component destruction failures
            console.warn('Error during component cleanup (safely handled):', e);
        }

        // No super.ngOnDestroy call needed in base class. Subclasses should override onAdaptableStateChanged for custom logic.
    }

    protected buildReportTitleKey(newName?: string) {
        const reportName = newName || this.report?.name || this.tabName || '';
        return `Report - ${this.reportTypeName}: ${reportName}`;
    }

    /**
     * Registers Adaptable event handlers. Call after Adaptable API is ready.
     * Handles event registration in one place.
     */
    protected _registerAdaptableEventHandlers(): void {
        if (this.adaptableApi?.eventApi) {
            this.adaptableApi.eventApi.on('AdaptableStateChanged', this._adaptableStateChangedHandler);
        }
    }

    /**
     * Called when the adaptable api is ready to perform
     * @param adaptableReady adaptable api event
     */
    protected adaptableReady(adaptableReady: AdaptableReadyInfo): void {
        // Sets adaptable api
        this.adaptableApi = adaptableReady.adaptableApi;
        // Register Adaptable event handlers here
        this._registerAdaptableEventHandlers();

        // Patch Excel exports
        patchExcelExport(this.gridApi, () => this.getExportReportTagLabel());
        // Patch CSV exports
        patchCsvExport(this.gridApi, () => this.getExportReportTagLabel());

        // Updates adaptable ready observable
        this.adaptableReady$.next(true);

        // Initialize adaptable state change detection
        this.initializeAdaptableStateChangeDetection();
        this._changeDetectorReference.detectChanges();
    }

    /**
     * Check if both grid and adaptable are ready
     * This is now a simple wrapper around the BehaviorSubjects' current values
     */
    protected bothComponentsReady(): boolean {
        return this.gridDataReady$.value && this.adaptableReady$.value;
    }

    /**
     * Gets the status message to display in the grid
     * This method should be overridden by derived classes to provide custom status messages
     * @returns The status message to display, or undefined if no message should be displayed
     */
    protected cancelClosingUnsavedReport() {
        this.isSaveDialogVisible = false;
        this._builderManagerService.deleteFromUnsavedWarningState({ open: false, tabId: this.currentTabId });
    }

    /**
     * Checks for selected cells and remove if there is any
     * @returns Promise
     */
    protected checkForSelectedCells(): Promise<void> {
        return new Promise<void>(resolve => {
            if (this.adaptableApi.gridApi.getSelectedCellInfo().gridCells.length) {
                this.adaptableApi.gridApi.deselectAll();
                this.safeAdd(
                    timer(300)
                        .pipe(take(1))
                        .subscribe(() => {
                            resolve();
                        })
                );
            } else {
                resolve();
            }
        });
    }

    /**
     * Checks if all components are ready and shows the grid
     * This is called when the allComponentsReady$ observable emits true
     * Now uses RxJS timer instead of setTimeout for better coordination
     */
    protected checkIfShouldShowGrid(): void {
        // Only proceed if all components are ready and we're not initializing
        if (!this.bothComponentsReady() || !this.columnSizingComplete$.value || this.isInitializing$.value) {
            return;
        }

        // Cancel any existing subscription to prevent multiple triggers
        if (this._showGridTimer) {
            this._showGridTimer.unsubscribe();
            this._showGridTimer = null;
        }

        // Use timer instead of setTimeout for better RxJS integration
        // Use our _safeSubscribe method which automatically handles takeUntil and cleanup
        this._showGridTimer = this.safeSubscribe(timer(150), () => {
            // Double-check that we have data before showing the grid
            if (this.gridApi) {
                // Ensure all column sizing is complete by forcing a grid resize
                if (this._needsFullInitialization) {
                    this._needsFullInitialization = false;
                }
            }

            // Only show if we're still loading (prevents flashing if already shown)
            if (this.isLoading()) {
                // Mark initial load as complete so the shouldBeLoading$ observable can take over
                this.isInitialLoadComplete$.next(true);
                this.isLoading.set(false);
            }
        });
    }

    /**
     * Confirmation of closing an unsaved report
     */
    protected closeUnsavedReport() {
        this._builderManagerService.deleteFromUnsavedWarningState({ open: false, tabId: this.currentTabId });
        this.closeUnsavedTab.emit(this.currentTabId);
    }

    /**
     * Sets a different status message in the grid status bar component
     * The default message is automatically displayed on the component
     * @param message message to display
     */
    protected setStatusMessage(message: string): void {
        // Safety check: ensure gridApi is initialized
        if (!this.gridApi) {
            console.warn('Grid API not fully initialized, skipping status message update');
            return;
        }

        const statusBarComponent = this.gridApi.getStatusPanel<IStatusBarMessageComp>('statusMessageComponentKey')!;
        statusBarComponent.setMessage(message);
        this._changeDetectorReference.detectChanges();
    }

    /**
     * Gets the Excel sheet name for exports
     * Gets row value for csv
     * This method can be overridden by derived classes to provide custom sheet names
     * @param tagLabel The default sheet name from the export context
     * @returns The Excel sheet name to use
     */
    protected getExportReportTagLabel(tagLabel?: string): string {
        return tagLabel || '';
    }

    protected initializeAdaptableStateChangeDetection() {
        const ignoreActions = new Set([
            'SYSTEM_SETTINGS_PANEL_SET',
            'POPUP_SHOW_SCREEN',
            'POPUP_HIDE_SCREEN',
            'POPUP_SHOW_ALERT',
            'POPUP_SHOW_CONFIRMATION',
            'POPUP_CANCEL_CONFIRMATION',
            'GRID_SET_SELECTED_CELLS',
            'EXPORT_APPLY',
            'DASHBOARD_SET_TITLE',
            'SET_SELECTED_CELLS'
        ]);

        const stateChanged$: Observable<AdaptableStateChangedInfo> = fromEventPattern(
            handler => this.adaptableApi.eventApi.on('AdaptableStateChanged', handler),
            handler => this.adaptableApi.eventApi.off('AdaptableStateChanged', handler)
        );

        this.safeAdd(
            this.isAdaptableLayoutReadyForStateChanges$
                .pipe(
                    switchMap(isReady => {
                        if (!isReady) {
                            // this is important to ensure skipUntil is
                            // called again when it changes from false to true
                            return EMPTY;
                        }
                        return stateChanged$.pipe(
                            filter(stateChange => !ignoreActions.has(stateChange.actionName)),
                            skipUntil(timer(2000)),
                            debounceTime(300)
                        );
                    })
                )
                .subscribe(() => {
                    this.store.dispatch(ReportTabActions.setUnsavedTabAction({ reportUUID: this.currentTabId }));
                })
        );
    }

    /**
     * Handler for AdaptableStateChanged events.
     *
     * Subclasses should override this method to implement custom event logic.
     * Do NOT register or cleanup events directly; this is managed by the base class.
     *
     * @param eventInfo - AdaptableStateChangedInfo event payload
     */
    protected onAdaptableStateChanged(eventInfo: AdaptableStateChangedInfo): void {
        // Default: no-op. Subclasses override for custom logic.
        if (eventInfo.actionName === 'LAYOUT_SELECT') {
            const currentLayout = this.adaptableApi.layoutApi.getCurrentLayout();
            // Validates if the selected layout is the default with empty definitions
            const isDefaultPivotLayout = this.adaptableApi.layoutApi.isCurrentLayoutPivot() && !currentLayout.PivotAggregationColumns?.length && !currentLayout.PivotGroupedColumns?.length;
            if (isDefaultPivotLayout) {
                this.adaptableApi.agGridApi.openToolPanel('columns');
            }
        }
        if (eventInfo.actionName === 'CALCULATED_COLUMN_ADD') {
            const calculatedColumn = (eventInfo.action as { calculatedColumn?: CalculatedColumn })?.calculatedColumn!;
            this.adaptableApi.layoutApi.addColumnToCurrentLayout(calculatedColumn.ColumnId);
        }
    }

    /**
     * Called when the AG Grid is ready
     * Sets up the grid API and initializes data loading
     * Now uses RxJS timer instead of setTimeout
     */
    protected onGridReady(params: GridReadyEvent): void {
        this.gridApi = params.api;

        // Set gridDataReady to true when data is loaded and rendered
        if (this.gridData.rows && this.gridData.rows.length > 0) {
            // Use timer instead of setTimeout for better RxJS integration
            this.safeAdd(
                timer(100)
                    .pipe(
                        take(1),
                        tap(() => {
                            // Update the BehaviorSubject
                            this.gridDataReady$.next(true);
                            // The checkIfShouldShowGrid will be called automatically via the allComponentsReady$ subscription
                            this._changeDetectorReference.detectChanges();
                        })
                    )
                    .subscribe()
            );
        }

        if (this.customStatusBarMessage) {
            this.setStatusMessage(this.customStatusBarMessage);
        }
    }

    /**
     * Safely adds a subscription to the component's subscription collection
     * and tracks it individually for better cleanup management.
     *
     * USE THIS METHOD WHEN:
     * 1. You have an existing Subscription object that needs to be tracked
     * 2. You're working with event-based subscriptions (like fromEvent or Adaptable events)
     * 3. You have complex subscription patterns that can't use safeSubscribe
     *
     * For standard Observable subscriptions, prefer using safeSubscribe instead.
     *
     * @param subscription The subscription to add and track
     */
    protected safeAdd(subscription: Subscription): void {
        if (subscription) {
            // Add to the main Subscription object
            this._subscriptions.add(subscription);
            // Also track individually
            this._individualSubscriptions.push(subscription);
        }
    }

    /**
     * Helper method to create a subscription with automatic cleanup using takeUntil.
     * This is the PREFERRED way to create subscriptions in components.
     *
     * USE THIS METHOD WHEN:
     * 1. You're subscribing to any Observable in your component
     * 2. You need automatic cleanup when the component is destroyed
     * 3. You want to ensure memory leaks are prevented
     *
     * Benefits over manual subscription + safeAdd:
     * - Automatically applies takeUntil with destroy$ Subject
     * - Handles proper tracking in subscription collections
     * - Provides a cleaner, more consistent API
     * - Prevents memory leaks even if developers forget to unsubscribe
     *
     * Example usage:
     * ```
     * this.safeSubscribe(
     *   this.someService.getData(),
     *   data => this.handleData(data)
     * );
     * ```
     *
     * @param observable The observable to subscribe to
     * @param observer The observer or next handler function
     * @returns The created subscription
     */
    protected safeSubscribe<T>(observable: Observable<T>, observer: Partial<Observer<T>> | ((value: T) => void)): Subscription {
        // Apply takeUntil to automatically complete when component is destroyed
        const subscription = observable.pipe(takeUntil(this.destroy$)).subscribe(observer);

        // Add to our subscription collections for tracking
        this.safeAdd(subscription);

        return subscription;
    }

    /**
     * Set grid data through Adaptable API considering the need of creating or migrating layouts
     */
    protected setGridData() {
        // Safety check: ensure adaptableApi is initialized
        if (!this.adaptableApi || !this.adaptableApi.agGridApi) {
            console.warn('Adaptable API not fully initialized, skipping grid data update');
            return;
        }
        this._checkLayoutAssignment();
        // get merged column ids and visibility before we alter the current adaptable layout state
        const mergedGridColumnIds = this.getMergedColumnIds();
        // get the currentLayout
        const currentLayout = this.adaptableApi.layoutApi.getCurrentLayout();

        this.adaptableApi.agGridApi.setGridOption('rowData', this.gridData.rows);
        this.adaptableApi.agGridApi.setGridOption('columnDefs', this.gridData.columns);

        // Case 1 - New report without layout
        if (!this.report.reportId && this.gridData.columns?.length) {
            const defaultTableLayout = this._createTableLayout(currentLayout);
            // Create and set layout with the given columns
            this.adaptableApi.layoutApi.createOrUpdateLayout({ ...defaultTableLayout, Name: ReportLayoutNames.TABLE_LAYOUT, TableColumns: mergedGridColumnIds });
            // this._completeInitialization(); // we call _completeInitialization in updateGridState

            // PFD-7147 - make avg of GRPs, NEP and TRPs
            this.updateGridState(this.adaptableApi.stateApi.getAllState());
        }
        // Case 2 - Old report with empty state or rerunning current report - create or update layout and state
        else if (!this.report.settings || (JSON.stringify(this.report.settings) === '{}' && this.gridData.columns?.length)) {
            if (currentLayout.TableColumns && currentLayout.TableColumns.length > 0) {
                this.adaptableApi.layoutApi.createOrUpdateLayout({ ...(currentLayout as TableLayout), Name: ReportLayoutNames.TABLE_LAYOUT, TableColumns: mergedGridColumnIds });
            } else {
                this.adaptableApi.layoutApi.createOrUpdateLayout({ ...(currentLayout as PivotLayout), Name: ReportLayoutNames.PIVOT_LAYOUT, PivotColumns: mergedGridColumnIds });
            }
            this.updateGridState(this.adaptableApi.stateApi.getAllState());
        }
        // Case 3 - New and old reports with saved state
        else if (this.report.settings && JSON.stringify(this.report.settings) !== '{}') {
            // Validate old properties to migrate or not the saved state
            // this was uncommented because the saved state was not getting loaded
            // if (this.report.settings['Grid'] || this.report.settings['System'] || this.report.settings['Query']) {
            //     this._migrateExistingState();
            // } else {
            //     // apply saved state
            //     this.updateGridState();
            // }
            this._migrateExistingState();
        }
    }

    /**
     * @description Retrieves a merged and sorted list of unique column IDs from the grid's data and calculated columns.
     * @returns {string[]} An array of unique column IDs.
     */
    private getMergedColumnIds(): string[] {
        // incoming columns from the report edit
        const gridColumnIds: string[] = this.gridData.columns.map((col: ColDef) => col.colId);
        // calculated columns, we need these to set visibility
        const calculatedColumnIds: string[] = this.adaptableApi.calculatedColumnApi.getCalculatedColumns().map(col => col.ColumnId);
        // all columns from incoming plus calculated, are all the columns now in the report
        const allColumnIds: string[] = Array.from(new Set([...gridColumnIds, ...calculatedColumnIds]));
        // columns from the report prior to the edit. We get this so we can later check if the user may have removed or added new columns
        const previousColumnIds: string[] = this.adaptableApi.columnApi.getColumns().map(col => col.columnId);

        // sort columns according to layout (if we have a previous set of columns), placing any new columns not in layout at the end
        const sortedColumnIds = [...allColumnIds!].sort(function (a, b) {
            return (previousColumnIds.indexOf(a) >>> 0) - (previousColumnIds.indexOf(b) >>> 0);
        });
        return sortedColumnIds;
    }

    /**
     * Updates the grid state when adaptable API is ready
     * Handles column sizing and layout initialization
     * Now uses RxJS timer instead of setTimeout
     */
    protected updateGridState(migratedState?: AdaptableState): void {
        // Safety check: ensure adaptableApi is initialized
        if (!this.adaptableApi) {
            console.warn('Adaptable API not fully initialized, skipping grid state update');
            return;
        }

        // Reset column sizing flag before updating grid options
        this.columnSizingComplete$.next(false);

        // Ensure the dashboard title matches the assigned report name,
        const dashboard = this.report?.settings?.Dashboard;
        const title = this.buildReportTitleKey();
        if (dashboard?.DashboardTitle) {
            dashboard.DashboardTitle = title;
        }

        // Update migratedState DashboardTitle if available
        if (migratedState) {
            migratedState.Dashboard.DashboardTitle = title;
        }

        this._completeInitialization();
        // apply saved state
        this.safeAdd(
            timer(400)
                .pipe(
                    take(1),
                    tap(() => {
                        this.adaptableApi.stateApi.reloadInitialState(migratedState ?? this.report.settings);
                    })
                )
                .subscribe()
        );
    }

    /**
     * Updates the title in the report
     * @param prefix optional prefix in the report name
     * @param reportName
     * @returns The title
     */
    protected updateReportName(reportName = this.report.name) {
        const title = this.buildReportTitleKey(reportName);
        this.adaptableApi?.dashboardApi?.setDashboardTitle(title);
        this.adaptableOpts = { ...this.adaptableOpts, adaptableId: title };

        // report may have been already updated with new name, so use tabName
        this.reportPreviousName = this.tabName || '';
        this.report.name = reportName;
        this.tabName = reportName;
    }

    /**
     * Expands or contracts row groups by group index / dimensions
     * @param row Clicked row
     * @param expands true for expanding and false for contracting
     */
    private _expandNodeByGroupLevel(row: IRowNode, expands: boolean): void {
        if (row.rowGroupIndex === null) {
            return;
        }
        this.gridApi.forEachNode(node => {
            if (node.rowGroupIndex === row.rowGroupIndex) {
                this.gridApi.setRowNodeExpanded(node, expands);
            }
        });
    }

    private _adaptableStateChangedHandler = (eventInfo: AdaptableStateChangedInfo) => {
        // Skip event handling if the component has been destroyed
        if (this._isDestroyed) {
            return;
        }

        // Process the event normally if the component is still active
        this.onAdaptableStateChanged(eventInfo);
    };

    /** Check if there are layouts without table columns (pivots) */
    private _checkExistentPivotLayout(): boolean {
        return !!this.adaptableApi.layoutApi.getLayouts().find(layout => !layout.TableColumns?.length);
    }

    /** Check if there are layouts with table columns (tables) */
    private _checkExistentTableLayout(): boolean {
        return !!this.adaptableApi.layoutApi.getLayouts().find(layout => layout.TableColumns?.length);
    }

    /**
     * Before building grid, checks if the empty pivot layout is not set
     * If so, assign a valid table layout
     */
    private _checkLayoutAssignment(): void {
        const currentLayout = this.adaptableApi.layoutApi.getCurrentLayout();
        if ((!currentLayout.PivotAggregationColumns?.length && currentLayout.Name === ReportLayoutNames.PIVOT_LAYOUT) || currentLayout.Name === ReportLayoutNamesLegacy.DEFAULT_LAYOUT) {
            const tableLayout = this.adaptableApi.layoutApi.getLayouts().find(l => l.TableColumns?.length);
            this.adaptableApi.layoutApi.setLayout(tableLayout?.Name ?? ReportLayoutNames.TABLE_LAYOUT);
        }
    }

    private _checkOldLayoutNames(): void {
        const currentLayoutName = this.adaptableApi.layoutApi.getCurrentLayoutName();
        const layouts = this.adaptableApi.layoutApi.getLayouts();
        const defaultLayout = layouts.find(layout => layout.Name === ReportLayoutNamesLegacy.DEFAULT_LAYOUT);
        const defaultPivot = layouts.find(layout => layout.Name === ReportLayoutNamesLegacy.DEFAULT_LAYOUT);
        if (defaultLayout) {
            // change the name of Default Layout to Table Layout
            this.adaptableApi.layoutApi.createOrUpdateLayout({
                ...defaultLayout,
                Name: ReportLayoutNames.TABLE_LAYOUT
            });
        }
        if (defaultPivot) {
            // change the name of Default Pivot to Pivot Layout
            this.adaptableApi.layoutApi.createOrUpdateLayout({
                ...defaultPivot,
                Name: ReportLayoutNames.PIVOT_LAYOUT
            });
        }
        // switch to the appropriate layout name
        if (currentLayoutName === ReportLayoutNamesLegacy.DEFAULT_LAYOUT) {
            this.adaptableApi.layoutApi.setLayout(ReportLayoutNames.TABLE_LAYOUT);
        } else if (currentLayoutName === ReportLayoutNamesLegacy.DEFAULT_LAYOUT) {
            this.adaptableApi.layoutApi.setLayout(ReportLayoutNames.PIVOT_LAYOUT);
        }
    }

    /**
     * Expand or collapse nodes
     * @param expanded current expansion state
     */
    private _collapseExpand(expanded: boolean): void {
        this.gridApi.forEachNode(node => {
            node.expanded = expanded;
        });
        this.gridApi.onGroupExpandedOrCollapsed();
    }

    /**
     * Finishes the observables to show grid
     */
    private _completeInitialization() {
        // Mark initialization as complete
        this.isInitializing$.next(false);

        // Notify to initializeAdaptableStateChangeDetection that we're ready to receive state changes.
        this.isAdaptableLayoutReadyForStateChanges$.next(true);

        // Use timer instead of setTimeout for better RxJS integration
        // Adaptable takes some time to fully load the applied settings in reloadInitialState, but doesn't have observables to notify its ending
        this.safeAdd(
            timer(150)
                .pipe(
                    take(1),
                    tap(() => {
                        this.columnSizingComplete$.next(true);
                        // This is the original configuration. In some point, it is being overridden and 'Layout' is lost
                        this.adaptableApi.dashboardApi.setPinnedToolbars(['Layout', 'Export']);
                        // Creates a pivot layout if none exists
                        if (!this._checkExistentPivotLayout()) this.adaptableApi.layoutApi.createOrUpdateLayout(this._createPivotLayout());
                        // Creates a table layout if none exists
                        if (!this._checkExistentTableLayout()) this.adaptableApi.layoutApi.createOrUpdateLayout(this._createTableLayout());
                        this._checkOldLayoutNames();
                        this._collapseExpand(true);
                        // The checkIfShouldShowGrid will be called automatically via the allComponentsReady$ subscription
                        this._changeDetectorReference.markForCheck();
                    })
                )
                .subscribe()
        );
    }

    /**
     *  Create an empty pivot layout
     */
    private _createPivotLayout(): PivotLayout {
        const pivotLayout = (defaultAdaptableOptions?.initialState?.Layout?.Layouts?.find(layout => layout.Name === ReportLayoutNames.PIVOT_LAYOUT) as PivotLayout) || {
            Name: 'Pivot Layout',
            PivotGroupedColumns: [],
            PivotColumns: [],
            PivotAggregationColumns: [],
            SuppressAggFuncInHeader: true,
            GrandTotalRow: 'pinnedBottom'
        };
        return pivotLayout;
    }

    /**
     *  Create a new table layout with the columns from the grid
     */
    private _createTableLayout(currentLayout?: TableLayout | PivotLayout): TableLayout {
        const defaultTableLayout = (defaultAdaptableOptions?.initialState?.Layout?.Layouts?.find(layout => layout.Name === ReportLayoutNames.TABLE_LAYOUT) as TableLayout) || {
            Name: 'Table Layout',
            TableColumns: [],
            AutoSizeColumns: true,
            SuppressAggFuncInHeader: true,
            GrandTotalRow: 'pinnedBottom'
        };
        const defaultTableAggregationColumns = currentLayout?.TableAggregationColumns || [];
        const newTableLayout: TableLayout = {
            ...defaultTableLayout,
            TableColumns: this.gridData.columns.map((c: ColDef) => c.colId ?? ''),
            // PFD-7309 - If TableAggregationColumns aren't defined, table layout won't show totals
            TableAggregationColumns: this.gridData.columns
                .filter((col: ColDef) => col.enableValue)
                .map((col: ColDef) => {
                    const colAgg = defaultTableAggregationColumns.find(c => c.ColumnId === col.colId);
                    if (colAgg) {
                        return colAgg;
                    }
                    // PFD-7147 - make avg of GRPs, NEP and TRPs
                    if (col.colId && ReportBaseComponent.AVG_AGGREGATION_COLUMNS.includes(col.colId)) {
                        return {
                            ColumnId: col.colId,
                            AggFunc: 'avg'
                        };
                    }
                    return {
                        ColumnId: col.colId,
                        AggFunc: 'sum'
                    };
                })
        };
        return newTableLayout;
    }

    /**
     * Migrates adaptable state from older versions to guarantee compatibility
     * After migrating, call the saving to update the state for future uses
     */
    private _migrateExistingState(): void {
        if (!this.report.settings || JSON.stringify(this.report.settings) === '{}') {
            return;
        }

        // For some reason, previous and now invalid properties are not removed automatically
        const { Grid, System, Query, Notes, ...validState } = this.report.settings;

        // Previous FormatColumns objects have the DataTypes as 'Number' (Upper N) and are not automatically migrated
        validState.FormatColumn.FormatColumns = validState.FormatColumn.FormatColumns?.map(f => {
            if (f.Scope && 'DataTypes' in f.Scope) {
                f.Scope.DataTypes = f.Scope.DataTypes.map(dt => (dt !== 'text' && dt !== 'boolean' && dt !== 'date' ? 'number' : dt));
            }
            return f;
        });
        // migrate the report state
        const migratedState: AdaptableState = AdaptableUpgradeHelper.migrateAdaptableState(validState, { fromVersion: 19, toVersion: 20 });

        // get the current layout index
        const currentLayoutIndex = migratedState.Layout.Layouts.findIndex(layout => layout.Name === migratedState.Layout.CurrentLayout);

        // If table layout does not exist, update a new table layout with latest columns
        const tableLayoutIndex = migratedState.Layout.Layouts.findIndex(layout => layout.Name === ReportLayoutNames.TABLE_LAYOUT);
        migratedState.Layout.Layouts[tableLayoutIndex === -1 ? migratedState.Layout.Layouts.length : tableLayoutIndex] = this._createTableLayout(migratedState.Layout.Layouts[currentLayoutIndex]);

        // For older reports with a single layout that may be either table or pivot, change the layout to Pivot Layout if no table columns are found
        if (migratedState.Layout.Layouts.length === 1 && migratedState.Layout.CurrentLayout === ReportLayoutNamesLegacy.DEFAULT_LAYOUT) {
            if (!migratedState.Layout.Layouts[currentLayoutIndex].TableColumns?.length) {
                migratedState.Layout.Layouts[currentLayoutIndex].Name = ReportLayoutNames.PIVOT_LAYOUT;
                // set the current layout to the new pivot layout name
                migratedState.Layout.CurrentLayout = ReportLayoutNames.PIVOT_LAYOUT;
                // Add a table layout as first layout since none exists
                migratedState.Layout.Layouts.unshift(this._createTableLayout());
            } else {
                migratedState.Layout.Layouts[currentLayoutIndex].Name = ReportLayoutNames.TABLE_LAYOUT;
            }
        }
        // checking the update of columns during edit migrated states and make sure we haven't lost calculated columns
        migratedState.Layout.Layouts[currentLayoutIndex].TableColumns = [...new Set([...(validState.Layout.Layouts[currentLayoutIndex].TableColumns || []), ...this.gridData.columns.map((c: ColDef) => c.colId ?? '')])];

        // apply migrated state
        this.updateGridState(migratedState);

        // Save the migrated state on the report to update
        // this.saveReport(this.report);
    }
}
